import { useState, useEffect } from "react";
import { uid, CLIENT_COLORS } from "../constants";
import TopBar from "../components/TopBar";
import Avatar from "../components/Avatar";
import Badge from "../components/Badge";
import FieldRow from "../components/FieldRow";
import Toast from "../components/Toast";
import { apiFetch } from "../utils/api";

const Settings = ({ topBarProps, profile, setProfile, clients, currentRole, announcements: annProp, setAnnouncements: setAnnProp, initialTab = 0, requirePasswordReset = false, onPasswordChanged }) => {
  const [activeTab, setActiveTab] = useState(requirePasswordReset ? 3 : initialTab);
  const [toast, setToast] = useState("");
  const [toastType, setToastType] = useState("");
  const showToast = (msg, type = "") => { setToast(msg); setToastType(type); setTimeout(() => { setToast(""); setToastType(""); }, 2800); };

  // Auto-switch to Profile/Password tab when arriving with a temp password
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (requirePasswordReset) setActiveTab(3); }, [requirePasswordReset]);

  // ── User management state ──
  const [users, setUsers] = useState([]);
  const [showAddUser, setShowAddUser] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [resetPwdUser, setResetPwdUser] = useState(null);
  // Use parent announcements state so bell updates live
  const announcements = annProp || [];
  const setAnnouncements = setAnnProp || (() => { });
  const [newAnn, setNewAnn] = useState({ title: "", body: "", priority: "Medium" });

  const ROLE_LABEL = { super_admin: "Super Admin", admin: "Admin", sub_admin: "Sub-Admin" };
  const ROLE_DB = { "Super Admin": "super_admin", "Admin": "admin", "Sub-Admin": "sub_admin" };

  // The root super-admin email. Only this account can edit/delete/reset/unlock
  // itself; no other super-admin may touch it. The root account can still
  // manage every other super-admin.
  const ROOT_ADMIN_EMAIL = "admin@dolluzcorp.com";
  const isRootEmail = e => String(e || "").trim().toLowerCase() === ROOT_ADMIN_EMAIL;
  const currentIsRoot = isRootEmail(profile?.email);
  // Returns true when the current user is allowed to manage the given target.
  // Blocks any non-root super-admin from acting on the root admin account.
  const canActOnTarget = (u) => isRootEmail(u.email) ? currentIsRoot : true;

  useEffect(() => {
    apiFetch("/api/settings/users")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) setUsers(d.data.map(u => ({
          ...u,
          role: ROLE_LABEL[u.role] || u.role,
          status: u.is_active ? "Active" : "Inactive",
          lastLogin: u.last_login_at
            ? new Date(u.last_login_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
            : "Never",
          firstName: u.first_name || "",
          lastName: u.last_name || "",
          avatar: u.avatar_initials || (u.name ? u.name[0].toUpperCase() : "?"),
          designation: u.designation || "",
          phone: u.phone || "",
          company: u.company || "",
          timezone: u.timezone || "Asia/Kolkata",
        })));
      })
      .catch(() => { });
    // system_settings now loaded in dedicated useEffect below
    // Announcements loaded by DolluzEPRPortal — no local fetch needed
  }, []); // eslint-disable-line
  const blankUser = { firstName: "", lastName: "", name: "", email: "", role: "Sub-Admin", designation: "", phone: "", company: "", timezone: "Asia/Kolkata", status: "Active" };
  const [newUser, setNewUser] = useState(blankUser);
  const [assignedClientIds, setAssignedClientIds] = useState([]);

  // Roles creatable by current user. Super Admins can now mint other Super
  // Admins (with root-admin guard enforced on edit/delete instead).
  const creatableRoles = currentRole === "Super Admin" ? ["Super Admin", "Admin", "Sub-Admin"] : currentRole === "Admin" ? ["Sub-Admin"] : [];

  const roleColor = r => ({ "Super Admin": "#E8520A", "Admin": "#3B82F6", "Sub-Admin": "#8B5CF6" }[r] || "#94A3B8");
  const roleBg = r => ({ "Super Admin": "#FFF5F0", "Admin": "#EFF6FF", "Sub-Admin": "#F5F3FF" }[r] || "#F8FAFC");

  const saveUser = async () => {
    if (!newUser.firstName || !newUser.lastName || !newUser.email) { showToast("First name, last name and email are required", "error"); return; }
    newUser.name = [newUser.firstName, newUser.lastName].filter(Boolean).join(" ");
    if (editUser) {
      const prev = [...users];
      setUsers(p => p.map(u => u.id === editUser ? { ...u, ...newUser } : u));
      setShowAddUser(false); setEditUser(null); setNewUser(blankUser);
      try {
        const payload = { ...newUser, first_name: newUser.firstName, last_name: newUser.lastName, role: ROLE_DB[newUser.role] || newUser.role };
        const res = await apiFetch(`/api/settings/users/${editUser}`, { method: "PUT", body: JSON.stringify(payload) });
        const d = await res.json();
        if (!d.success) { setUsers(prev); showToast("Error: " + (d.message || "Update failed"), "error"); return; }
        // Save client assignments if sub-admin and current user is super-admin or admin
        if (newUser.role === "Sub-Admin" && (currentRole === "Super Admin" || currentRole === "Admin")) {
          await apiFetch(`/api/settings/users/${editUser}/clients`, { method: "PUT", body: JSON.stringify({ client_ids: assignedClientIds }) });
        }
        // If the edited user is the currently-logged-in admin, sync the sidebar
        // profile chip so name/designation/phone/etc. reflect immediately.
        if (profile && editUser === profile.id) {
          const fullName = [newUser.firstName, newUser.lastName].filter(Boolean).join(" ");
          setProfile(p => ({
            ...p,
            firstName: newUser.firstName,
            lastName: newUser.lastName,
            name: fullName,
            avatar: newUser.firstName ? newUser.firstName[0].toUpperCase() : (p.avatar || "?"),
            designation: newUser.designation,
            phone: newUser.phone,
            company: newUser.company,
            timezone: newUser.timezone,
          }));
        }
        showToast([newUser.firstName, newUser.lastName].filter(Boolean).join(" ") + " updated");
      } catch (e) { setUsers(prev); showToast("Network error — update not saved", "error"); }
    } else {
      const tempUser = { ...newUser, id: "U" + uid(), lastLogin: "Never", name: [newUser.firstName, newUser.lastName].filter(Boolean).join(" "), avatar: newUser.firstName ? newUser.firstName[0].toUpperCase() : "?" };
      const prev = [...users];
      setUsers(p => [...p, tempUser]);
      setShowAddUser(false); setEditUser(null); setNewUser(blankUser);
      try {
        const payload = { ...newUser, first_name: newUser.firstName, last_name: newUser.lastName, role: ROLE_DB[newUser.role] || newUser.role };
        const res = await apiFetch("/api/settings/users", { method: "POST", body: JSON.stringify(payload) });
        const d = await res.json();
        if (!d.success) { setUsers(prev); showToast("Error: " + (d.message || "Add failed"), "error"); return; }
        const newId = d.data?.id || d.id;
        if (newId) {
          setUsers(p => p.map(u => u.id === tempUser.id ? { ...u, id: newId } : u));
          // Save client assignments if sub-admin and current user is super-admin or admin
          if (newUser.role === "Sub-Admin" && (currentRole === "Super Admin" || currentRole === "Admin") && assignedClientIds.length > 0) {
            await apiFetch(`/api/settings/users/${newId}/clients`, { method: "PUT", body: JSON.stringify({ client_ids: assignedClientIds }) });
          }
        }
        showToast([newUser.firstName, newUser.lastName].filter(Boolean).join(" ") + " added — welcome email sent");
      } catch (e) { setUsers(prev); showToast("Network error — user not saved", "error"); }
    }
  };

  const toggleUserStatus = async id => {
    const user = users.find(u => u.id === id);
    const isActive = user?.status === "Active" ? 0 : 1;
    const newStatus = isActive === 1 ? "Active" : "Inactive";
    const prev = [...users];
    setUsers(p => p.map(u => u.id === id ? { ...u, status: newStatus } : u));
    try {
      const res = await apiFetch(`/api/settings/users/${id}`, { method: "PUT", body: JSON.stringify({ is_active: isActive }) });
      const d = await res.json();
      if (!d.success) { setUsers(prev); showToast("Error: " + (d.message || "Status update failed"), "error"); }
      else showToast(user.name + " " + (isActive ? "activated" : "deactivated"));
    } catch (e) { setUsers(prev); showToast("Network error — status not changed", "error"); }
  };
  const deleteUser = async id => {
    const user = users.find(u => u.id === id);
    const prev = [...users];
    setUsers(p => p.filter(u => u.id !== id));
    try {
      const res = await apiFetch(`/api/settings/users/${id}`, { method: "DELETE" });
      const d = await res.json();
      if (!d.success) { setUsers(prev); showToast("Error: " + (d.message || "Delete failed"), "error"); return; }
      showToast((user?.name || "User") + " permanently deleted");
    } catch (e) { setUsers(prev); showToast("Network error — user not deleted", "error"); }
  };

  const postAnnouncement = async () => {
    if (!newAnn.title || !newAnn.body) { showToast("Title and message are required", "error"); return; }
    try {
      const res = await apiFetch("/api/announcements", { method: "POST", body: JSON.stringify({ title: newAnn.title, body: newAnn.body, priority: newAnn.priority }) });
      const d = await res.json();
      if (!d.success) { showToast("Error: " + (d.message || "Post failed"), "error"); return; }
      // Use real DB id and confirmed type from response
      const savedType = d.type || (newAnn.priority === "High" ? "alert" : newAnn.priority === "Medium" ? "warning" : "info");
      const entry = {
        id: d.id,
        title: newAnn.title,
        body: newAnn.body,
        type: savedType,
        is_read_by_me: false,
        read_count: 0,
        created_at: new Date().toISOString(),
        created_by: profile.id || 1,
      };
      setAnnouncements(p => [entry, ...p]);
      setNewAnn({ title: "", body: "", priority: "Medium" });
      showToast("Announcement posted — visible in notification bell");
    } catch (e) { showToast("Network error — announcement not posted", "error"); }
  };
  // Announcement edit state
  const [editAnn, setEditAnn] = useState(null); // { id, title, body, priority }
  const [confirmDeleteAnn, setConfirmDeleteAnn] = useState(null); // { id, title }
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null); // { id, name, role }

  const deleteAnnouncement = async (id) => {
    const prev = [...announcements];
    setAnnouncements(p => p.filter(a => a.id !== id));
    try {
      const res = await apiFetch(`/api/announcements/${id}`, { method: "DELETE" });
      const d = await res.json();
      if (!d.success) { setAnnouncements(prev); showToast("Delete failed: " + (d.message || ""), "error"); }
      else showToast("Announcement deleted");
    } catch (e) { setAnnouncements(prev); showToast("Network error — not deleted", "error"); }
  };

  const saveEditAnn = async () => {
    if (!editAnn.title || !editAnn.body) { showToast("Title and message are required", "error"); return; }
    try {
      const res = await apiFetch(`/api/announcements/${editAnn.id}`, {
        method: "PUT",
        body: JSON.stringify({ title: editAnn.title, body: editAnn.body, priority: editAnn.priority }),
      });
      const d = await res.json();
      if (!d.success) { showToast("Update failed: " + (d.message || ""), "error"); return; }
      const savedType = d.type || (editAnn.priority === "High" ? "alert" : editAnn.priority === "Medium" ? "warning" : "info");
      setAnnouncements(p => p.map(a => a.id === editAnn.id
        ? { ...a, title: editAnn.title, body: editAnn.body, type: savedType }
        : a
      ));
      setEditAnn(null);
      showToast("Announcement updated");
    } catch (e) { showToast("Network error — not updated", "error"); }
  };

  // Can current user reset password for target user?
  // Super Admin: any user except the root admin (unless self is root).
  // Admin: only Sub-Admins.
  const canResetPwd = (u) => {
    if (currentRole === "Super Admin") return canActOnTarget(u);
    if (currentRole === "Admin") return u.role === "Sub-Admin";
    return false;
  };


  // Accept both UI labels (High/Medium/Low) and DB values (alert/warning/info)
  const normPrio = p => ({ alert: "High", warning: "Medium", info: "Low" }[p] || p || "Low");
  const prioColor = p => ({ High: "#EF4444", Medium: "#F59E0B", Low: "#10B981" }[normPrio(p)] || "#94A3B8");
  const prioBg = p => ({ High: "#FEF2F2", Medium: "#FFFBEB", Low: "#F0FDF4" }[normPrio(p)] || "#F8FAFC");

  // ── Security state ──
  const [sec, setSec] = useState({ sessionTimeout: "30", maxLoginAttempts: "5", lockoutDuration: "15", enforce2FA: false, passwordMinLen: "8", requireSpecialChar: true, requireUppercase: true, requireNumber: true, logoutOnClose: false });

  // ── System Config state (Tab 4) — keyed to system_settings DB keys ──────────
  const [sysConfig, setSysConfig] = useState({
    otp_expiry_minutes: "10", otp_length: "6", otp_rate_limit_max: "5",
    smtp_host: "", smtp_port: "587", email_from_name: "", email_from_address: "",
    company_name: "", portal_title: "Dolluz Corp EPR Portal",
    stakeholder_form_url: "", portal_url: "",
  });

  // Load system config from API on mount (merged with getSettings call)
  useEffect(() => {
    apiFetch("/api/settings")
      .then(r => r.json())
      .then(d => {
        if (d.success && d.data) {
          // Flatten grouped settings into key:value for sysConfig
          const flat = {};
          Object.values(d.data).forEach(group => {
            Object.entries(group).forEach(([k, v]) => { flat[k] = v.value || ""; });
          });
          setSysConfig(p => ({ ...p, ...flat }));
          // Also hydrate sec state from DB settings
          setSec(p => ({
            ...p,
            passwordMinLen: flat.password_min_length || p.passwordMinLen,
            maxLoginAttempts: flat.max_login_attempts || p.maxLoginAttempts,
            lockoutDuration: flat.lockout_minutes || p.lockoutDuration,
            sessionTimeout: flat.session_timeout_mins || p.sessionTimeout,
            requireSpecialChar: flat.require_special_char === "true",
            requireUppercase: flat.require_uppercase === "true",
            requireNumber: flat.require_number === "true",
          }));
        }
      })
      .catch(() => { });
  }, []); // eslint-disable-line

  const updSysConfig = (k, v) => setSysConfig(p => ({ ...p, [k]: v }));

  const saveSysConfig = async (keys) => {
    const settings = {};
    keys.forEach(k => { settings[k] = sysConfig[k]; });
    try {
      const res = await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ settings }) });
      const d = await res.json();
      if (!d.success) { showToast("Error: " + (d.message || "Save failed"), "error"); return; }
      showToast("Settings saved successfully");
    } catch (e) { showToast("Network error — settings not saved", "error"); }
  };

  // Save the Security & Access tab. Only writes the four fields that
  // authController actually reads from system_settings at runtime:
  // password_min_length + the three require_* flags. The other fields shown
  // on the tab (max_login_attempts, lockout_minutes, session_timeout_mins,
  // 2FA, auto-logout) are read-only because the runtime sources them from
  // .env at boot — saving them would silently diverge UI state from
  // enforcement, so we skip them entirely.
  const saveSecuritySettings = async () => {
    const SEC_KEY_MAP = {
      passwordMinLen:     "password_min_length",
      requireSpecialChar: "require_special_char",
      requireUppercase:   "require_uppercase",
      requireNumber:      "require_number",
    };
    const settings = {};
    for (const [camel, snake] of Object.entries(SEC_KEY_MAP)) {
      const v = sec[camel];
      if (v === undefined || v === null) continue;
      // system_settings.setting_value is VARCHAR — stringify everything,
      // booleans land as "true" / "false" which authController already reads.
      settings[snake] = typeof v === "boolean" ? String(v) : String(v);
    }
    try {
      const res = await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ settings }) });
      const d = await res.json();
      if (!d.success) {
        showToast("Error: " + (d.message || "Save failed"), "error");
        return;
      }
      showToast("Security settings saved");
    } catch (e) {
      showToast("Network error — security settings not saved", "error");
    }
  };
  const updSec = (k, v) => setSec(p => ({ ...p, [k]: v }));

  // ── Profile state lives in App root (shared with Sidebar) ──
  const [pwForm, setPwForm] = useState({ current: "", newpw: "", confirm: "" });
  const [showPw, setShowPw] = useState({ current: false, newpw: false, confirm: false });

  const Tab = ({ i, label, icon }) => (
    <button onClick={() => setActiveTab(i)} style={{
      padding: "12px 18px", border: "none", cursor: "pointer", fontSize: 12, fontWeight: activeTab === i ? 700 : 500,
      background: activeTab === i ? "#FFF5F0" : "#fff", color: activeTab === i ? "#E8520A" : "#475569",
      borderBottom: activeTab === i ? "2.5px solid #E8520A" : "2.5px solid transparent",
      display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap"
    }}>
      <span dangerouslySetInnerHTML={{ __html: icon }} /> {label}
    </button>
  );

  return (
    <div className="fade-in">
      <TopBar title="System Settings" subtitle="Admin panel — users, security, announcements, system config" {...topBarProps} />

      <div className="card" style={{ marginBottom: 0, overflow: "hidden" }}>
        <div style={{ display: "flex", borderBottom: "1px solid #F1F5F9", overflowX: "auto" }}>
          <Tab i={0} label="User Management" icon="&#128101;" />
          <Tab i={1} label="Security & Access" icon="&#128274;" />
          <Tab i={2} label="Announcements" icon="&#128226;" />
          <Tab i={3} label="My Profile" icon="&#128100;" />
          {currentRole === "Super Admin" && <Tab i={4} label="System Config" icon="&#9881;" />}
        </div>

        {/* ── Tab 0: User Management ── */}
        {activeTab === 0 && (
          <div style={{ padding: "24px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0D1B2A" }}>Portal Users</div>
                <div style={{ fontSize: 12, color: "#64748B", marginTop: 2 }}>Manage who can log in and their roles</div>
              </div>
              {creatableRoles.length > 0 && (
                <button className="btn-primary" style={{ fontSize: 12 }} onClick={() => { setEditUser(null); setNewUser(blankUser); setAssignedClientIds([]); setShowAddUser(true); }}>+ Add User</button>
              )}
            </div>

            {/* ── Role Permission Matrix ── */}
            <div style={{ background: "#F8FAFC", borderRadius: 10, padding: "16px 18px", marginBottom: 20, border: "1px solid #E2E8F0" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#475569", marginBottom: 12 }}>Role Permission Matrix</div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ minWidth: 560, fontSize: 11, borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: "left", padding: "6px 12px", width: 200, color: "#0D1B2A" }}>Capability</th>
                      {["Super Admin", "Admin", "Sub-Admin"].map(r => (
                        <th key={r} style={{
                          textAlign: "center", padding: "6px 16px",
                          color: roleColor(r), background: roleBg(r), borderRadius: 6
                        }}>{r}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Create Super Admins", true, false, false],
                      ["Create Admins", true, false, false],
                      ["Create Sub-Admins", true, true, false],
                      ["Edit / Delete Other Super Admins", true, false, false],
                      ["Reset Admin Passwords", true, false, false],
                      ["Reset Sub-Admin Passwords", true, true, false],
                      ["Delete Any Record", true, false, false],
                      ["Add / Edit Employees", true, true, false],
                      ["Activate / Close Cycles", true, true, false],
                      ["Send Emails & Reminders", true, true, false],
                      ["View Employees", true, true, true],
                      ["View Allocation %", true, true, true],
                      ["View Cycles", true, true, true],
                      ["Export Excel / PDF", true, true, true],
                      ["Access Settings", true, false, false],
                    ].map(([cap, sa, ad, sub]) => (
                      <tr key={cap} style={{ borderBottom: "1px solid #F1F5F9" }}>
                        <td style={{ padding: "7px 12px", fontSize: 12, color: "#475569", fontWeight: 500 }}>{cap}</td>
                        {[sa, ad, sub].map((allowed, i) => (
                          <td key={i} style={{ textAlign: "center", padding: "7px 16px" }}>
                            {allowed
                              ? <span style={{ color: "#10B981", fontSize: 15, fontWeight: 800 }}>✓</span>
                              : <span style={{ color: "#E2E8F0", fontSize: 13 }}>✗</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ marginTop: 10, fontSize: 11, color: "#94A3B8", display: "flex", gap: 16, flexWrap: "wrap" }}>
                <span>🔒 Sub-Admin scope is restricted to assigned clients &amp; stakeholders only</span>
                <span>🚫 Admin delete actions show disabled tooltip pointing to Super Admin</span>
                <span>👑 The root Super Admin ({ROOT_ADMIN_EMAIL}) can only be edited / deleted by itself</span>
              </div>
            </div>

            {/* ── User List ── */}
            <table>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {

                  // Delete button state for this user. Super Admin can now
                  // delete other Super Admins, but not the root admin (unless
                  // self is root) and not themselves.
                  const isSelf = u.id === profile?.id;
                  const canDelete = currentRole === "Super Admin" && canActOnTarget(u) && !isSelf;
                  const showDisabledDelete = currentRole === "Admin" && u.role === "Sub-Admin";

                  return (
                    <tr key={u.id}>
                      <td>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div style={{
                            width: 34, height: 34, borderRadius: "50%",
                            background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0
                          }}>
                            {u.avatar}
                          </div>
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>{u.name}</div>
                            <div style={{ fontSize: 11, color: "#94A3B8" }}>{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span style={{
                          background: roleBg(u.role), color: roleColor(u.role),
                          borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 700
                        }}>{u.role}</span>
                      </td>
                      <td>
                        <span style={{
                          background: u.status === "Active" ? "#F0FDF4" : "#F8FAFC",
                          color: u.status === "Active" ? "#10B981" : "#94A3B8",
                          borderRadius: 20, padding: "3px 10px", fontSize: 11, fontWeight: 600
                        }}>{u.status}</span>
                      </td>
                      <td style={{ fontSize: 12, color: "#475569" }}>{u.lastLogin}</td>
                      <td>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                          {/* Protected-row indicator — shown when the current
                              user cannot perform ANY action on this row
                              (root-admin guard). Makes the empty Actions
                              column self-explanatory instead of looking broken. */}
                          {currentRole === "Super Admin" && !canActOnTarget(u) && (
                            <span title="Root Super Admin — only this account can edit itself"
                              style={{
                                fontSize: 10, fontWeight: 700, color: "#64748B",
                                background: "#F1F5F9", border: "1px solid #E2E8F0",
                                borderRadius: 100, padding: "3px 9px", whiteSpace: "nowrap",
                              }}>
                              👑 Protected
                            </span>
                          )}
                          {/* Edit — Super Admin can edit anyone except the root
                              admin (unless self IS root). Self-edit is allowed
                              (the backend whitelists fields, so role/email
                              changes are still safe). Admin can edit Sub-Admins. */}
                          {((currentRole === "Super Admin" && canActOnTarget(u)) ||
                            (currentRole === "Admin" && u.role === "Sub-Admin")) && (
                              <button className="btn-ghost" style={{ fontSize: 11 }} onClick={() => {
                                setEditUser(u.id);
                                setNewUser({
                                  firstName: u.firstName || u.name?.split(" ")[0] || "",
                                  lastName: u.lastName || u.name?.split(" ").slice(1).join(" ") || "",
                                  name: u.name, email: u.email, role: u.role,
                                  designation: u.designation || "",
                                  phone: u.phone || "",
                                  company: u.company || "",
                                  timezone: u.timezone || "Asia/Kolkata",
                                  status: u.status,
                                });
                                setAssignedClientIds([]);
                                if (u.role === "Sub-Admin") {
                                  apiFetch(`/api/settings/users/${u.id}/clients`)
                                    .then(r => r.json())
                                    .then(d => { if (d.success) setAssignedClientIds(d.data || []); })
                                    .catch(() => { });
                                }
                                setShowAddUser(true);
                              }}>Edit</button>
                            )}

                          {/* Reset Password */}
                          {canResetPwd(u) && !isSelf && (
                            <button className="btn-ghost" style={{ fontSize: 11, color: "#3B82F6" }}
                              onClick={() => setResetPwdUser(u)}>Reset Pwd</button>
                          )}

                          {/* Activate / Deactivate */}
                          {!isSelf && ((currentRole === "Super Admin" && canActOnTarget(u)) || (currentRole === "Admin" && u.role === "Sub-Admin")) && (
                            <button className="btn-ghost" style={{ fontSize: 11, color: u.status === "Active" ? "#F59E0B" : "#10B981" }}
                              onClick={() => toggleUserStatus(u.id)}>
                              {u.status === "Active" ? "Deactivate" : "Activate"}
                            </button>
                          )}

                          {/* Delete — Super Admin: enabled | Admin: disabled with tooltip | Sub-Admin: hidden */}
                          {canDelete && (
                            <button className="btn-ghost" style={{ fontSize: 11, color: "#EF4444" }}
                              onClick={() => setConfirmDeleteUser({ id: u.id, name: u.name, role: u.role })}>Remove</button>
                          )}
                          {showDisabledDelete && (
                            <button className="btn-ghost" disabled title="Only Super Admin can delete users"
                              style={{ fontSize: 11, color: "#CBD5E1", cursor: "not-allowed", opacity: 0.5 }}>Remove</button>
                          )}

                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Add / Edit User Modal ── */}
            {showAddUser && (
              <div className="modal-overlay" style={{ left: 236 }}>
                <div className="modal" style={{ maxWidth: 500, width: "min(500px, calc(100% - 32px))" }}>
                  <div style={{ padding: "22px 26px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#0D1B2A" }}>
                      {editUser ? "Edit User" : "Add Portal User"}
                    </div>
                    <button onClick={() => setShowAddUser(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>&#215;</button>
                  </div>
                  <div style={{ padding: "22px 26px", display: "flex", flexDirection: "column", gap: 14, maxHeight: "70vh", overflowY: "auto" }}>
                    {/* Name */}
                    <div>
                      <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>First Name *</label>
                      <input className="inp" type="text" value={newUser.firstName} onChange={e => setNewUser(p => ({ ...p, firstName: e.target.value, avatar: e.target.value[0] ? e.target.value[0].toUpperCase() : "" }))} placeholder="First Name" />
                    </div>
                    <div style={{ marginBottom: 14 }}>
                      <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Last Name *</label>
                      <input className="inp" type="text" value={newUser.lastName} onChange={e => setNewUser(p => ({ ...p, lastName: e.target.value }))} placeholder="Last Name" />
                    </div>
                    {/* Email */}
                    <div>
                      <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Email Address *</label>
                      <input className="inp" type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} placeholder="user@dolluzcorp.com" disabled={!!editUser} style={editUser ? { background: "#F8FAFC", color: "#94A3B8" } : {}} />
                    </div>
                    {/* Designation & Phone — side by side */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Designation</label>
                        <input className="inp" type="text" value={newUser.designation} onChange={e => setNewUser(p => ({ ...p, designation: e.target.value }))} placeholder="e.g. HR Manager" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Phone</label>
                        <input className="inp" type="tel" value={newUser.phone} onChange={e => setNewUser(p => ({ ...p, phone: e.target.value.replace(/[^0-9+\-\s]/g, "") }))} placeholder="+91 9876543210" />
                      </div>
                    </div>
                    {/* Company & Timezone — side by side */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Company</label>
                        <input className="inp" type="text" value={newUser.company} onChange={e => setNewUser(p => ({ ...p, company: e.target.value }))} placeholder="Company name" />
                      </div>
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Timezone</label>
                        <select className="inp" value={newUser.timezone} onChange={e => setNewUser(p => ({ ...p, timezone: e.target.value }))}>
                          {["Asia/Kolkata", "Asia/Dubai", "Asia/Singapore", "Asia/Tokyo", "Europe/London", "Europe/Paris", "America/New_York", "America/Chicago", "America/Los_Angeles", "UTC"].map(tz => (
                            <option key={tz} value={tz}>{tz}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    {/* Role — only show if multiple options */}
                    {creatableRoles.length > 1 && (
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Role *</label>
                        <select className="inp" value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                          {creatableRoles.map(r => <option key={r}>{r}</option>)}
                        </select>
                      </div>
                    )}
                    {creatableRoles.length === 1 && (
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Role</label>
                        <div style={{
                          padding: "9px 12px", background: "#F8FAFC", borderRadius: 8, border: "1px solid #E2E8F0",
                          fontSize: 13, color: roleColor(creatableRoles[0]), fontWeight: 700
                        }}>{creatableRoles[0]}</div>
                      </div>
                    )}

                    {/* Status */}
                    <div>
                      <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 5 }}>Status</label>
                      <select className="inp" value={newUser.status} onChange={e => setNewUser(p => ({ ...p, status: e.target.value }))}>
                        <option>Active</option>
                        <option>Inactive</option>
                      </select>
                    </div>

                    {/* Client Scope Assignment — for Sub-Admin, visible to Super Admin and Admin */}
                    {newUser.role === "Sub-Admin" && (currentRole === "Super Admin" || currentRole === "Admin") && (
                      <div>
                        <label style={{ fontSize: 12, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 6 }}>
                          Assigned Clients{" "}
                          <span style={{ color: "#94A3B8", fontWeight: 400 }}>(Sub-Admin will only see these clients)</span>
                        </label>
                        <div style={{ border: "1px solid #E2E8F0", borderRadius: 8, maxHeight: 180, overflowY: "auto", padding: "6px 0", background: "#FAFBFC" }}>
                          {(clients || []).length === 0 ? (
                            <div style={{ padding: "10px 14px", fontSize: 12, color: "#94A3B8" }}>No clients available</div>
                          ) : (clients || []).map(c => (
                            <label key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 14px", cursor: "pointer", fontSize: 13 }}>
                              <input
                                type="checkbox"
                                checked={assignedClientIds.includes(c.id)}
                                onChange={e => setAssignedClientIds(p => e.target.checked ? [...p, c.id] : p.filter(id => id !== c.id))}
                                style={{ width: 15, height: 15, accentColor: "#6366F1", cursor: "pointer" }}
                              />
                              <span style={{ fontWeight: 600, color: "#0D1B2A" }}>{c.name}</span>
                              <span style={{ color: "#94A3B8", fontSize: 11 }}>({c.id})</span>
                            </label>
                          ))}
                        </div>
                        {assignedClientIds.length === 0 && (
                          <div style={{ fontSize: 11, color: "#F59E0B", marginTop: 6 }}>
                            ⚠ No clients assigned — Sub-Admin will see no data
                          </div>
                        )}
                      </div>
                    )}

                    <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#166534" }}>
                      {editUser ? "Changes take effect immediately." : "A welcome email with login credentials will be sent automatically."}
                    </div>
                  </div>
                  <div style={{ padding: "0 26px 22px", display: "flex", gap: 8 }}>
                    <button className="btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={saveUser}>
                      {editUser ? "Save Changes" : "Add User"}
                    </button>
                    <button className="btn-secondary" onClick={() => setShowAddUser(false)}>Cancel</button>
                  </div>
                </div>
              </div>
            )}

            {/* ── Password Reset Modal ── */}
            {resetPwdUser && (
              <div className="modal-overlay" style={{ left: 236 }}>
                <div className="modal" style={{ maxWidth: 420, width: "min(420px, calc(100% - 32px))" }}>
                  <div style={{ padding: "22px 26px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#0D1B2A" }}>Reset Password</div>
                    <button onClick={() => setResetPwdUser(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#94A3B8" }}>&#215;</button>
                  </div>
                  <div style={{ padding: "22px 26px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18, background: "#F8FAFC", borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{
                        width: 38, height: 38, borderRadius: "50%", background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
                        display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#fff"
                      }}>
                        {resetPwdUser.avatar}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0D1B2A" }}>{resetPwdUser.name}</div>
                        <div style={{ fontSize: 11, color: "#94A3B8" }}>{resetPwdUser.email}</div>
                        <span style={{
                          background: roleBg(resetPwdUser.role), color: roleColor(resetPwdUser.role),
                          borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700
                        }}>{resetPwdUser.role}</span>
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: "#475569", marginBottom: 16, lineHeight: 1.6 }}>
                      A password reset link will be sent to <strong>{resetPwdUser.email}</strong>.
                      The user must click the link within 24 hours to set a new password.
                    </div>
                    <div style={{ background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#991B1B", marginBottom: 16 }}>
                      ⚠ The user's current session will be invalidated immediately.
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn-primary" style={{ flex: 1, justifyContent: "center" }} onClick={async () => {
                        try {
                          const res = await apiFetch(`/api/settings/users/${resetPwdUser.id}/reset-password`, { method: "POST" });
                          const d = await res.json();
                          if (!d.success) { showToast("Error: " + (d.message || "Reset failed"), "error"); return; }
                          showToast("Password reset — new temporary password sent to " + resetPwdUser.email);
                          setResetPwdUser(null);
                        } catch (e) { showToast("Network error — reset not sent", "error"); }
                      }}>Send Reset Link</button>
                      <button className="btn-secondary" onClick={() => setResetPwdUser(null)}>Cancel</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Tab 1: Security & Access ── */}
        {activeTab === 1 && (
          <div style={{ padding: "24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
              {(() => {
                // Read-only chip used on fields that are NOT consumed by the
                // running server (read from .env at boot or not consumed at all).
                const RO_BADGE = (
                  <span title="Read-only — managed by the server administrator."
                    style={{
                      fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                      background: "#F1F5F9", color: "#64748B",
                      border: "1px solid #E2E8F0", borderRadius: 100,
                      padding: "2px 8px", letterSpacing: 0.4, marginLeft: 8, whiteSpace: "nowrap"
                    }}>
                    Read-only
                  </span>
                );
                const roLabel = (text) => (
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "flex", alignItems: "center", marginBottom: 5 }}>
                    {text}{RO_BADGE}
                  </label>
                );
                const roNumberInput = (key) => (
                  <input className="inp" type="number" value={sec[key]} readOnly disabled
                    style={{ maxWidth: 120, background: "#F8FAFC", color: "#475569", cursor: "not-allowed" }} />
                );
                const roCheckbox = (label, key) => (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, opacity: 0.6 }}>
                    <input type="checkbox" checked={sec[key]} disabled
                      style={{ width: 16, height: 16, cursor: "not-allowed" }} />
                    <label style={{ fontSize: 13, color: "#94A3B8", cursor: "not-allowed" }}>{label}</label>
                    {RO_BADGE}
                  </div>
                );
                return (
                  <>
                    {/* Password Policy */}
                    <div>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 16 }}>Password Policy</div>

                      {/* Minimum Length — read-only */}
                      <div style={{ marginBottom: 14 }}>
                        {roLabel("Minimum Length")}
                        {roNumberInput("passwordMinLen")}
                      </div>

                      {/* Max Login Attempts — read-only */}
                      <div style={{ marginBottom: 14 }}>
                        {roLabel("Max Login Attempts")}
                        {roNumberInput("maxLoginAttempts")}
                      </div>

                      {/* Lockout Duration — read-only */}
                      <div style={{ marginBottom: 14 }}>
                        {roLabel("Lockout Duration (min)")}
                        {roNumberInput("lockoutDuration")}
                      </div>

                      {/* Require flags — editable, all three are consumed by authController */}
                      {[
                        ["Require Special Character", "requireSpecialChar"],
                        ["Require Uppercase Letter", "requireUppercase"],
                        ["Require Numeric Digit", "requireNumber"],
                      ].map(([l, k]) => (
                        <div key={k} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                          <input type="checkbox" checked={sec[k]} onChange={e => updSec(k, e.target.checked)} style={{ width: 16, height: 16 }} />
                          <label style={{ fontSize: 13, color: "#475569", cursor: "pointer" }}>{l}</label>
                        </div>
                      ))}
                    </div>

                    {/* Session & 2FA — all read-only */}
                    <div>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 16 }}>Session & Authentication</div>

                      <div style={{ marginBottom: 14 }}>
                        {roLabel("Session Timeout (minutes)")}
                        {roNumberInput("sessionTimeout")}
                      </div>

                      {roCheckbox("Enforce Two-Factor Auth (2FA)", "enforce2FA")}
                      {roCheckbox("Auto-logout on browser close", "logoutOnClose")}

                      <div style={{ marginTop: 20, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#991B1B", marginBottom: 6 }}>Admin Login Security</div>
                        <div style={{ fontSize: 12, color: "#7F1D1D" }}>Admin sessions require re-authentication after {sec.sessionTimeout} minutes of inactivity. Failed attempts beyond {sec.maxLoginAttempts} will lock the account for {sec.lockoutDuration} minutes.</div>
                      </div>
                      <div style={{ marginTop: 16, background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "12px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#166534", marginBottom: 6 }}>Audit Log</div>
                        <div style={{ fontSize: 11, color: "#166534" }}>Audit log entries will appear here as users take actions in the portal.</div>
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
            {currentRole === "Super Admin" && (
              <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveSecuritySettings}>Save Security Settings</button>
              </div>
            )}
          </div>
        )}

        {/* ── Tab 2: Announcements ── */}
        {activeTab === 2 && (
          <div style={{ padding: "24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 20 }}>
              {/* Compose */}
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 16 }}>New Announcement</div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>Title *</label>
                  <input className="inp" value={newAnn.title} onChange={e => setNewAnn(p => ({ ...p, title: e.target.value }))} placeholder="Announcement title" />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>Priority</label>
                  <select className="inp" value={newAnn.priority} onChange={e => setNewAnn(p => ({ ...p, priority: e.target.value }))}>
                    {["High", "Medium", "Low"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>Message *</label>
                  <textarea className="inp" rows={5} value={newAnn.body}
                    onChange={e => setNewAnn(p => ({ ...p, body: e.target.value }))}
                    placeholder="Write your announcement here..."
                    style={{ resize: "vertical", fontFamily: "'DM Sans',sans-serif" }} />
                </div>
                <div style={{ background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 8, padding: "10px 14px", fontSize: 12, color: "#1E40AF", marginBottom: 14 }}>
                  Will be broadcast to all {users.filter(u => u.status === "Active").length} active portal users on the next login.
                </div>
                <button className="btn-primary" style={{ width: "100%", justifyContent: "center", fontSize: 13 }} onClick={postAnnouncement}>
                  📢 Post Announcement
                </button>
              </div>
              {/* History */}
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 16 }}>Posted Announcements ({announcements.length})</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {announcements.length === 0 && (
                    <div style={{ fontSize: 12, color: "#94A3B8", fontStyle: "italic", padding: "12px 0" }}>No announcements yet.</div>
                  )}
                  {announcements.map(a => {
                    const prio = normPrio(a.type || a.priority || "info");
                    const dateStr = a.created_at
                      ? new Date(a.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                      : (a.date || "");
                    const isEditing = editAnn && editAnn.id === a.id;
                    return (
                      <div key={a.id} style={{
                        background: isEditing ? "#FAFBFF" : "#F8FAFC",
                        borderRadius: 10, padding: "14px 16px",
                        border: isEditing ? `1.5px solid ${prioColor(prio)}` : "1px solid #F1F5F9",
                        borderLeft: `3px solid ${prioColor(prio)}`
                      }}>
                        {isEditing ? (
                          /* ── Edit mode ── */
                          <div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 4 }}>TITLE</label>
                              <input className="inp" value={editAnn.title}
                                onChange={e => setEditAnn(p => ({ ...p, title: e.target.value }))}
                                style={{ fontSize: 13 }} />
                            </div>
                            <div style={{ marginBottom: 8 }}>
                              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 4 }}>PRIORITY</label>
                              <select className="inp" value={editAnn.priority}
                                onChange={e => setEditAnn(p => ({ ...p, priority: e.target.value }))}
                                style={{ fontSize: 13 }}>
                                {["High", "Medium", "Low"].map(p => <option key={p}>{p}</option>)}
                              </select>
                            </div>
                            <div style={{ marginBottom: 10 }}>
                              <label style={{ fontSize: 11, color: "#64748B", fontWeight: 600, display: "block", marginBottom: 4 }}>MESSAGE</label>
                              <textarea className="inp" rows={3} value={editAnn.body}
                                onChange={e => setEditAnn(p => ({ ...p, body: e.target.value }))}
                                style={{ resize: "vertical", fontFamily: "'DM Sans',sans-serif", fontSize: 12 }} />
                            </div>
                            <div style={{ display: "flex", gap: 8 }}>
                              <button className="btn-primary" style={{ fontSize: 12 }} onClick={saveEditAnn}>Save Changes</button>
                              <button className="btn-secondary" style={{ fontSize: 12 }} onClick={() => setEditAnn(null)}>Cancel</button>
                            </div>
                          </div>
                        ) : (
                          /* ── View mode ── */
                          <>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                              <div style={{ fontWeight: 600, fontSize: 13, color: "#0D1B2A", flex: 1 }}>{a.title}</div>
                              <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                <span style={{ background: prioBg(prio), color: prioColor(prio), borderRadius: 20, padding: "2px 8px", fontSize: 10, fontWeight: 700 }}>{prio}</span>
                                <button onClick={() => setEditAnn({ id: a.id, title: a.title, body: a.body, priority: prio })}
                                  style={{ background: "none", border: "1px solid #E2E8F0", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#475569", cursor: "pointer", fontWeight: 500 }}>
                                  Edit
                                </button>
                                <button onClick={() => setConfirmDeleteAnn({ id: a.id, title: a.title })}
                                  style={{ background: "none", border: "1px solid #FECACA", borderRadius: 6, padding: "3px 8px", fontSize: 11, color: "#EF4444", cursor: "pointer", fontWeight: 500 }}>
                                  Delete
                                </button>
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: "#64748B", marginTop: 5, lineHeight: 1.6 }}>{a.body}</div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: 11, color: "#94A3B8" }}>
                              <span>{dateStr}</span>
                              <span>{a.read_count || 0} user{(a.read_count || 0) !== 1 ? "s" : ""} read</span>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 3: My Profile ── */}
        {activeTab === 3 && (
          <div style={{ padding: "24px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
              {/* Profile info */}
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, padding: "16px", background: "linear-gradient(135deg,#0D1B2A,#1E3A5F)", borderRadius: 12 }}>
                  <div style={{
                    width: 54, height: 54, borderRadius: "50%", background: "linear-gradient(135deg,#E8520A,#FF8C5A)",
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, color: "#fff", flexShrink: 0
                  }}>{profile.avatar || "S"}</div>
                  <div>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#fff" }}>{profile.name}</div>
                    <div style={{ fontSize: 12, color: "rgba(255,255,255,.6)" }}>{profile.designation} · {profile.company}</div>
                    <div style={{ fontSize: 11, color: "rgba(255,255,255,.4)", marginTop: 2 }}>{profile.email}</div>
                  </div>
                </div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 14 }}>Edit Profile</div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>First Name *</label>
                  <input className="inp" value={profile.firstName || ""} onChange={e => setProfile(p => ({ ...p, firstName: e.target.value, name: [e.target.value, p.lastName].filter(Boolean).join(" "), avatar: e.target.value ? e.target.value[0].toUpperCase() : "S" }))} />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>Last Name *</label>
                  <input className="inp" value={profile.lastName || ""} onChange={e => setProfile(p => ({ ...p, lastName: e.target.value, name: [p.firstName, e.target.value].filter(Boolean).join(" ") }))} />
                </div>
                {[["Phone", "phone"], ["Designation", "designation"], ["Company", "company"], ["Timezone", "timezone"]].map(([l, k]) => (
                  <div key={k} style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>{l}</label>
                    <input className="inp" value={profile[k] || ""} onChange={e => setProfile(p => ({ ...p, [k]: e.target.value }))} />
                  </div>
                ))}
                <div style={{ marginBottom: 12 }}>
                  <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>Email (read-only)</label>
                  <input className="inp" value={profile.email} readOnly style={{ background: "#F8FAFC", color: "#94A3B8" }} />
                </div>
                <button className="btn-primary" style={{ width: "100%", justifyContent: "center", fontSize: 13 }}
                  onClick={async () => {
                    if (!profile.firstName || !profile.lastName) { showToast("First name and last name are required", "error"); return; }
                    try {
                      const res = await apiFetch("/api/settings/profile", { method: "PUT", body: JSON.stringify({ ...profile, first_name: profile.firstName, last_name: profile.lastName }) });
                      const d = await res.json();
                      if (!d.success) { showToast("Error: " + (d.message || "Profile update failed"), "error"); return; }
                      showToast("Profile updated successfully");
                    } catch (e) { showToast("Network error — profile not saved", "error"); }
                  }}>Save Profile</button>
              </div>
              {/* Password change */}
              <div>
                <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 14 }}>Change Password</div>
                {[["Current Password", "current"], ["New Password", "newpw"], ["Confirm New Password", "confirm"]].map(([l, k]) => (
                  <div key={k} style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>{l}</label>
                    <div style={{ position: "relative" }}>
                      <input className="inp" type={showPw[k] ? "text" : "password"} value={pwForm[k]}
                        onChange={e => setPwForm(p => ({ ...p, [k]: e.target.value }))}
                        onPaste={e => { e.preventDefault(); const pasted = e.clipboardData.getData("text"); setPwForm(p => ({ ...p, [k]: pasted.trim() })); }}
                        placeholder={l} style={{ width: "100%", paddingRight: 38 }} />
                      <button type="button" onClick={() => setShowPw(p => ({ ...p, [k]: !p[k] }))}
                        style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center", padding: 0 }}>
                        {showPw[k]
                          ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" /><path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" /><line x1="1" y1="1" x2="23" y2="23" /></svg>
                          : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>
                        }
                      </button>
                    </div>
                  </div>
                ))}
                <div style={{ background: "#F8FAFC", borderRadius: 8, padding: "12px 14px", marginBottom: 16 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#475569", marginBottom: 6 }}>Password Requirements:</div>
                  {["At least 8 characters", "One uppercase letter", "One number", "One special character"].map((r, i) => (
                    <div key={i} style={{ fontSize: 11, color: "#64748B", marginBottom: 3 }}>✓ {r}</div>
                  ))}
                </div>
                <button className="btn-primary" style={{ width: "100%", justifyContent: "center", fontSize: 13 }}
                  onClick={async () => {
                    if (!pwForm.current || !pwForm.newpw) { showToast("All password fields are required", "error"); return; }
                    if (pwForm.newpw !== pwForm.confirm) { showToast("New passwords do not match", "error"); return; }
                    if (pwForm.newpw.length < 8) { showToast("New password must be at least 8 characters", "error"); return; }
                    try {
                      const res = await apiFetch("/api/auth/change-password", {
                        method: "POST",
                        body: JSON.stringify({ currentPassword: pwForm.current, newPassword: pwForm.newpw }),
                      });
                      const d = await res.json();
                      if (!d.success) { showToast("Error: " + (d.message || "Password change failed"), "error"); return; }
                      setPwForm({ current: "", newpw: "", confirm: "" });
                      showToast("Password changed successfully");
                      if (onPasswordChanged) onPasswordChanged();
                    } catch (e) { showToast("Network error — password not changed", "error"); }
                  }}>Update Password</button>

                <div style={{ marginTop: 24, borderTop: "1px solid #F1F5F9", paddingTop: 20 }}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#0D1B2A", marginBottom: 14 }}>Forgot Password Flow</div>
                  <div style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", borderRadius: 8, padding: "14px 16px", fontSize: 12, color: "#166534", lineHeight: 1.8 }}>
                    <strong>How it works:</strong><br />
                    1. Click "Send Reset Link" below.<br />
                    2. An OTP + reset link is emailed to your registered email.<br />
                    3. OTP expires in 10 minutes.<br />
                    4. After verification, set a new password.<br />
                    5. All active sessions are invalidated.
                  </div>
                  <button className="btn-secondary" style={{ marginTop: 12, width: "100%", justifyContent: "center", fontSize: 12 }}
                    onClick={async () => {
                      try {
                        const res = await apiFetch("/api/auth/forgot-password", { method: "POST", body: JSON.stringify({ email: profile.email }) });
                        const d = await res.json();
                        showToast(d.success ? "Reset OTP sent to " + profile.email : (d.message || "Failed to send reset OTP"));
                      } catch (e) { showToast("Network error — reset link not sent", "error"); }
                    }}>
                    📧 Send Reset Link to My Email
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab 4: System Config (Super Admin only) ── */}
        {activeTab === 4 && currentRole === "Super Admin" && (() => {
          // System Config values are infrastructure-level and read-only in the
          // UI. They are read once at server startup, so changing them here
          // would not take effect on the running server.
          const RO_BADGE = (
            <span title="Read-only — managed by the server administrator."
              style={{
                fontSize: 9, fontWeight: 700, textTransform: "uppercase",
                background: "#F1F5F9", color: "#64748B",
                border: "1px solid #E2E8F0", borderRadius: 100,
                padding: "2px 8px", letterSpacing: 0.4, marginLeft: 8, whiteSpace: "nowrap"
              }}>
              Read-only
            </span>
          );
          const roField = (label, key, type = "text") => (
            <div key={key} style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 12, color: "#64748B", fontWeight: 500, display: "block", marginBottom: 5 }}>{label}</label>
              <input className="inp" type={type} value={sysConfig[key] || ""}
                readOnly disabled
                style={{
                  background: "#F8FAFC", color: "#475569", cursor: "not-allowed",
                  fontFamily: type === "number" ? undefined : "'JetBrains Mono',monospace",
                  fontSize: 12,
                  maxWidth: type === "number" ? 120 : undefined,
                }} />
            </div>
          );
          const sectionHeader = (icon, title) => (
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
              <span style={{ fontSize: 20 }} dangerouslySetInnerHTML={{ __html: icon }} />
              <span style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0D1B2A" }}>{title}</span>
              {RO_BADGE}
            </div>
          );
          return (
            <div style={{ padding: "24px" }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>

                {/* OTP Configuration */}
                <div className="card" style={{ padding: "22px 24px" }}>
                  {sectionHeader("&#128274;", "OTP Configuration")}
                  {roField("OTP Validity (minutes)", "otp_expiry_minutes", "number")}
                  {roField("OTP Max Attempts", "otp_rate_limit_max", "number")}
                  {roField("OTP Length (digits)", "otp_length", "number")}
                </div>

                {/* Email Configuration */}
                <div className="card" style={{ padding: "22px 24px" }}>
                  {sectionHeader("&#128231;", "Email Configuration")}
                  {roField("SMTP Host", "smtp_host")}
                  {roField("SMTP Port", "smtp_port")}
                  {roField("Sender Name", "email_from_name")}
                  {roField("Sender Email", "email_from_address")}
                </div>

                {/* Portal URLs */}
                <div className="card" style={{ padding: "22px 24px" }}>
                  {sectionHeader("&#127757;", "Portal URLs")}
                  {roField("Portal URL (admin)", "portal_url")}
                  {roField("Stakeholder Form URL", "stakeholder_form_url")}
                </div>

                {/* Branding */}
                <div className="card" style={{ padding: "22px 24px" }}>
                  {sectionHeader("&#127912;", "Branding")}
                  {roField("Company Name", "company_name")}
                  {roField("Portal Title", "portal_title")}
                </div>

              </div>
            </div>
          );
        })()}
      </div>
      {toast && <Toast msg={toast} type={toastType} />}

      {/* ── Delete User Confirm Modal ── */}
      {confirmDeleteUser && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "28px 32px", width: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", textAlign: "center"
          }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#FEF2F2", border: "2px solid #FECACA", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, margin: "0 auto 14px" }}>🗑️</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#0D1B2A", marginBottom: 8 }}>
              Delete User?
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 10, lineHeight: 1.5 }}>
              You are about to permanently delete:
            </div>
            <div style={{
              fontSize: 13, fontWeight: 700, color: "#1E293B",
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, padding: "10px 14px", marginBottom: 8
            }}>
              {confirmDeleteUser.name}
            </div>
            <div style={{
              display: "inline-block", fontSize: 11, fontWeight: 700,
              background: roleBg(confirmDeleteUser.role), color: roleColor(confirmDeleteUser.role),
              borderRadius: 100, padding: "2px 10px", marginBottom: 16
            }}>
              {confirmDeleteUser.role}
            </div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 24 }}>
              This action is permanent and cannot be undone. The user will lose all access immediately.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn-secondary" style={{ fontSize: 13, minWidth: 100 }}
                onClick={() => setConfirmDeleteUser(null)}>
                Cancel
              </button>
              <button style={{ fontSize: 13, minWidth: 120, background: "#EF4444", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { deleteUser(confirmDeleteUser.id); setConfirmDeleteUser(null); }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Delete Announcement Confirm Modal ── */}
      {confirmDeleteAnn && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
        }}>
          <div style={{
            background: "#fff", borderRadius: 16, padding: "28px 32px", width: 420,
            boxShadow: "0 20px 60px rgba(0,0,0,0.2)", textAlign: "center"
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>🗑️</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 16, color: "#0D1B2A", marginBottom: 8 }}>
              Delete Announcement?
            </div>
            <div style={{ fontSize: 13, color: "#64748B", marginBottom: 6, lineHeight: 1.5 }}>
              You are about to delete:
            </div>
            <div style={{
              fontSize: 13, fontWeight: 600, color: "#1E293B",
              background: "#FEF2F2", border: "1px solid #FECACA",
              borderRadius: 8, padding: "8px 14px", marginBottom: 20
            }}>
              "{confirmDeleteAnn.title}"
            </div>
            <div style={{ fontSize: 12, color: "#94A3B8", marginBottom: 24 }}>
              This cannot be undone. It will also be removed from all users' notification bells.
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
              <button className="btn-secondary" style={{ fontSize: 13, minWidth: 100 }}
                onClick={() => setConfirmDeleteAnn(null)}>
                Cancel
              </button>
              <button className="btn-danger" style={{ fontSize: 13, minWidth: 120, background: "#EF4444", color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 700, cursor: "pointer" }}
                onClick={() => { deleteAnnouncement(confirmDeleteAnn.id); setConfirmDeleteAnn(null); }}>
                Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;